// docs/137 HN: a suggested hero name in the forge, drawn from the species' own given names for the
// hero's gender and either a family name of that people or a byname the class earns. The pools live in
// content/names (HN4, checked by tools/names/check.mjs). Client-only; the server takes any name.
import human from '../../../../../../content/names/human.json';
import dwarf from '../../../../../../content/names/dwarf.json';
import elf from '../../../../../../content/names/elf.json';
import halfling from '../../../../../../content/names/halfling.json';
import orc from '../../../../../../content/names/orc.json';
import tiefling from '../../../../../../content/names/tiefling.json';
import dragonborn from '../../../../../../content/names/dragonborn.json';
import halfElf from '../../../../../../content/names/half-elf.json';
import gnome from '../../../../../../content/names/gnome.json';
import birdfolk from '../../../../../../content/names/birdfolk.json';
import bynames from '../../../../../../content/names/bynames.json';

/** A people's names: given names by gender, and the family (or clan, house, flock…) names they carry;
 *  `gendered` lists the family names that go with one gender only (Ivarsson, Thuridottir, Gro-Mash). */
export interface PeopleNames {
  male: readonly string[]; female: readonly string[]; family: readonly string[];
  gendered?: { male: readonly string[]; female: readonly string[] };
}

/** HN5: the hero's gender for names, as the forge's Voice code: 0 male (Masculine), 1 female (Feminine). */
export type NameGender = 0 | 1;

/** By species index (forge.ts `SPECIES`, sim `SPECIES_NAMES`). */
export const SPECIES_NAMES_POOL: readonly PeopleNames[] = [human, dwarf, elf, halfling, orc, tiefling, dragonborn, halfElf, gnome, birdfolk];

/** By class index (sim `CLASS_NAMES`): bynames the road gives a hero of that class. */
export const CLASS_BYNAMES: readonly (readonly string[])[] = [
  bynames.fighter, bynames.rogue, bynames.wizard, bynames.cleric, bynames.ranger, bynames.paladin, bynames.druid, bynames.bard,
];

/** HN5: bynames and family names that name a gender (Shieldmaiden, Greybeard, Ivarsson,
 *  Thuridottir) go only with that gender. */
const onlyFor = (g?: { male: readonly string[]; female: readonly string[] }): ReadonlyMap<string, NameGender> =>
  new Map([...(g?.male ?? []).map((n) => [n, 0] as const), ...(g?.female ?? []).map((n) => [n, 1] as const)]);
const BYNAME_ONLY_FOR = onlyFor(bynames.gendered);
const FAMILY_ONLY_FOR = SPECIES_NAMES_POOL.map((p) => onlyFor(p.gendered));
const fits = (only: ReadonlyMap<string, NameGender>, gender: NameGender | undefined) => (n: string) =>
  gender === undefined || (only.get(n) ?? gender) === gender;

/** Server's limit (fk-world `clean_name(_, 24)`); a suggestion never needs trimming. */
export const NAME_MAX = 24;

const pick = <T,>(xs: readonly T[], rng: () => number): T => xs[Math.floor(rng() * xs.length) % xs.length]!;
const peopleOf = (species: number): PeopleNames => SPECIES_NAMES_POOL[species] ?? SPECIES_NAMES_POOL[0]!;
const bynamesOf = (cls: number, gender?: NameGender): readonly string[] =>
  (CLASS_BYNAMES[cls] ?? CLASS_BYNAMES[0]!).filter(fits(BYNAME_ONLY_FOR, gender));
const familyOf = (species: number, gender?: NameGender): readonly string[] =>
  peopleOf(species).family.filter(fits(FAMILY_ONLY_FOR[species] ?? new Map(), gender));
export const givenNames = (species: number, gender: NameGender): readonly string[] => (gender === 1 ? peopleOf(species).female : peopleOf(species).male);

/** Could `name` be suggested for this species and class (and gender, if given)? A given name of the
 *  people, a space, then a family name of the people or a byname of the class (for the gender, if given). */
export function isSuggestion(name: string, species: number, cls: number, gender?: NameGender): boolean {
  const at = name.indexOf(' ');
  if (at < 0 || name.length > NAME_MAX) return false;
  const given = name.slice(0, at), rest = name.slice(at + 1);
  const p = peopleOf(species);
  const givenOk = gender === undefined ? p.male.includes(given) || p.female.includes(given) : givenNames(species, gender).includes(given);
  return givenOk && (familyOf(species, gender).includes(rest) || bynamesOf(cls, gender).includes(rest));
}

/** HN1/HN2/HN5: a name for this species, class and gender: a given name of the species for the gender
 *  and, about half the time, a byname of the class that fits the gender, else a family name of the
 *  species. Never one of `avoid` (the current suggestion, a name just forged) while another is left. */
export function suggestName(species: number, cls: number, gender: NameGender, rng: () => number = Math.random, avoid: readonly string[] = []): string {
  const given = givenNames(species, gender);
  const family = familyOf(species, gender);
  const by = bynamesOf(cls, gender);
  const no = new Set(avoid.map((n) => n.trim().toLowerCase()));
  let n = '';
  for (let i = 0; i < 48; i++) {
    n = `${pick(given, rng)} ${rng() < 0.5 ? pick(by, rng) : pick(family, rng)}`;
    if (n.length <= NAME_MAX && !no.has(n.toLowerCase())) return n;
  }
  return n.length <= NAME_MAX ? n : pick(given, rng);
}
